import { DpmsTypes } from "csd.phoenix.models/DpmsTypes";
import { EntityType, UNKNOWN_ENTITY_TYPE } from "../models/enums";
import { ISupportedEntityType } from "../models/CustomerSyncEntityTypes";
import { IEntityTypeDependency } from "../models/EntityTypeDependency";
import { loadConfig } from "../services/appConfigService";
import http from "../services/api/http";

const dpmsTypesControllerName = "DpmsTypes";

// Helper function to normalize entity type values from the backend.
// Backend should send string enum names ("PaymentTransactionType", "Patient", etc.).
// If you see numeric values, the backend needs JsonStringEnumConverter configured.
export function parseEntityType(value: string | number | any): EntityType {
  // If it's a string, use it directly
  if (typeof value === "string") return value;
  
  // If it's a number, convert to string (backend serialization issue - should be fixed there)
  if (typeof value === "number") {
    console.warn(`EntityType received as number (${value}). Backend should use JsonStringEnumConverter.`);
    return String(value);
  }
  
  // If it's an object with a nested property, extract it
  if (value && typeof value === "object") {
    const extracted = value.type || value.name || value.entityType;
    return extracted ? String(extracted) : UNKNOWN_ENTITY_TYPE;
  }
  
  // Fallback
  return UNKNOWN_ENTITY_TYPE;
}

// Helper function to convert API response entity types to normalized format
function convertEntityTypesInDependency(dep: any): IEntityTypeDependency {
  return {
    entityType: parseEntityType(dep.entityType),
    suggestedOrder: dep.suggestedOrder,
    dependsOn: (dep.dependsOn || []).map(parseEntityType),
    requiredBy: (dep.requiredBy || []).map(parseEntityType)
  };
}

export async function getSupportedEntityTypes(dpmsType: DpmsTypes): Promise<ISupportedEntityType[]> {
  const { gatewayAdminUrl } = await loadConfig();
  const url = gatewayAdminUrl
    .segment(dpmsTypesControllerName)
    .segment("/:dpmsType")
    .segment("/SupportedEntityTypes")
    .param({ dpmsType: dpmsType.toString() })
    .toString();

  const response = await http.get<any>(url);
  
  // Handle HTTP 204 No Content - return empty array
  if (!response.data) {
    return [];
  }
  
  // Convert API response to ISupportedEntityType array
  const supportedEntityTypes: ISupportedEntityType[] = (response.data.supportedEntityTypes || []).map((item: any) => ({
    type: parseEntityType(item.type),  // Normalize to string identifier
    displayName: item.displayName || item.type || "Unknown"
  }));
  
  return supportedEntityTypes;
}

export async function getEntityTypeDependencies(dpmsType: DpmsTypes): Promise<IEntityTypeDependency[]> {
  const { gatewayAdminUrl } = await loadConfig();
  const url = gatewayAdminUrl
    .segment(dpmsTypesControllerName)
    .segment("/:dpmsType")
    .segment("/EntityTypeDependencies")
    .param({ dpmsType: dpmsType.toString() })
    .toString();

  const { data } = await http.get<any[]>(url);
  // Convert string entity types to enum values
  return (data || []).map(convertEntityTypesInDependency);
}
